import { Switch, Route, useLocation } from 'wouter';
import { Toaster } from '@/components/ui/toaster';
import { TooltipProvider } from '@/components/ui/tooltip';
import { AuthProvider, QueryProvider } from '@/providers';
import { ThemeProvider } from '@/components/ui/theme-provider.tsx';
import { ModalContainer } from '@/components/shared/modals/modal-container.tsx';
import { DisclaimerDialog } from '@/components/shared/compliance/disclaimer-dialog';
import { AdminRouteGuard } from '@/components/shared/admin/admin-route-guard';
import { ImpersonationBanner } from '@/components/shared/admin/impersonation-banner';
import { lazy, Suspense, useEffect } from 'react';

const PortfolioAnalysis = lazy(() => import('@/pages/portfolio-analysis'));
const PortfolioAnalysisResult = lazy(() => import('@/pages/portfolio-analysis-id.tsx'));
const ChatPage = lazy(() => import('@/pages/chat'));
const VerifyEmail = lazy(() => import('@/pages/verify-email'));
const ResetPassword = lazy(() => import('@/pages/reset-password'));
const PricingPage = lazy(() => import('@/pages/pricing'));
const TermsOfUsePage = lazy(() => import('@/pages/terms-of-use'));
const PrivacyPolicyPage = lazy(() => import('@/pages/privacy-policy'));
const DisclaimerPage = lazy(() => import('@/pages/disclaimer'));
const CookiePolicyPage = lazy(() => import('@/pages/cookie-policy'));
const SubscriptionTermsPage = lazy(() => import('@/pages/subscription-terms'));
const PaymentSuccess = lazy(() => import('@/pages/payment-success'));
const TestPayment = lazy(() => import('@/pages/test-payment'));
const AccountPage = lazy(() => import('@/pages/account'));
const AdminOverview = lazy(() => import('@/pages/admin/admin-overview'));
const AdminUsers = lazy(() => import('@/pages/admin/admin-users'));
const AdminUserDetail = lazy(() => import('@/pages/admin/admin-user-detail'));
const AdminB2B = lazy(() => import('@/pages/admin/admin-b2b'));
const NotFound = lazy(() => import('@/pages/not-found'));

const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

if (window) {
  window.addEventListener('beforeunload', () => {
    clearSessionStorage();
  });
}

export function clearSessionStorage() {
  // Clear all welcome-sent keys from sessionStorage
  const keysToRemove: string[] = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && key.startsWith('welcome-sent-')) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => sessionStorage.removeItem(key));
}

function Router() {
  const [location] = useLocation();

  // Clear welcome message sessionStorage when navigating away from chat
  useEffect(() => {
    if (typeof window !== 'undefined' && !location.startsWith('/chat')) {
      clearSessionStorage();
    }
  }, [location]);

  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        <Route path="/" component={PortfolioAnalysis} />
        <Route path="/portfolio-analysis" component={PortfolioAnalysis} />
        <Route path="/portfolio-analysis/:analysisId" component={PortfolioAnalysisResult} />
        <Route path="/chat" component={ChatPage} />
        <Route path="/verify-email" component={VerifyEmail} />
        <Route path="/reset-password" component={ResetPassword} />
        <Route path="/pricing" component={PricingPage} />
        <Route path="/account" component={AccountPage} />
        <Route path="/payment-success" component={PaymentSuccess} />
        <Route path="/terms-of-use" component={TermsOfUsePage} />
        <Route path="/privacy-policy" component={PrivacyPolicyPage} />
        <Route path="/disclaimer" component={DisclaimerPage} />
        <Route path="/cookie-policy" component={CookiePolicyPage} />
        <Route path="/subscription-terms" component={SubscriptionTermsPage} />
        <Route path="/test-payment" component={TestPayment} />
        <Route path="/admin">
          <AdminRouteGuard><AdminOverview /></AdminRouteGuard>
        </Route>
        <Route path="/admin/users">
          <AdminRouteGuard><AdminUsers /></AdminRouteGuard>
        </Route>
        <Route path="/admin/users/:userId">
          <AdminRouteGuard><AdminUserDetail /></AdminRouteGuard>
        </Route>
        <Route path="/admin/b2b">
          <AdminRouteGuard><AdminB2B /></AdminRouteGuard>
        </Route>
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function App() {
  return (
    <ThemeProvider defaultTheme="dark" storageKey="aspis-theme">
      <QueryProvider>
        <AuthProvider>
          <TooltipProvider>
            <div className="flex flex-col min-h-screen">
              <ImpersonationBanner />
              <Toaster />
              <ModalContainer />
              <DisclaimerDialog />

              <Router />
            </div>
          </TooltipProvider>
        </AuthProvider>
      </QueryProvider>
    </ThemeProvider>
  );
}

export default App;
